import {
    bindFirst,
    createTSlatePlugin,
    type Element,
    type OmitFirst,
    type PluginConfig,
    type SlateEditor,
} from '@udecode/plate';

import { deleteSourceCaption, insertSourceCaption } from './transforms';
import type { TSourceCaptionElement } from './types';

function isAdjacentSourceCaption(editor: SlateEditor, element: Element, direction: 'before' | 'after'): boolean {
    const path = direction === 'before' ? editor.api.before(element) : editor.api.after(element);
    const entry = editor.api.block({ at: path });
    return !!(entry && entry[0].type === 'source-caption');
}

export type SourceCaptionConfig = PluginConfig<
    'source-caption',
    {},
    {
        sourceCaption: {
            isAbove: OmitFirst<(editor: SlateEditor, element: Element) => boolean>;
            isBelow: OmitFirst<(editor: SlateEditor, element: Element) => boolean>;
        }
    },
    {
        insert: {
            sourceCaption: OmitFirst<typeof insertSourceCaption>
        };
        remove: {
            sourceCaption: OmitFirst<typeof deleteSourceCaption>
        };
    }
>;

/** Enables support for source captions (zdroj) below figures and tables. */
export const BaseSourceCaptionPlugin = createTSlatePlugin<SourceCaptionConfig>({
    key: 'source-caption',
    node: {
        isElement: true,
        isInline: false
    },
    options: {}
})
    .extendEditorApi<SourceCaptionConfig['api']>(({ editor }) => ({
        sourceCaption: {
            isAbove: (element: Element) => isAdjacentSourceCaption(editor, element, 'before'),
            isBelow: (element: Element) => isAdjacentSourceCaption(editor, element, 'after'),
        }
    }))
    .extendEditorTransforms<SourceCaptionConfig['transforms']>(({ editor }) => ({
        insert: {
            sourceCaption: bindFirst(insertSourceCaption, editor),
        },
        remove: {
            sourceCaption: bindFirst(deleteSourceCaption, editor),
        },
    }))
    .overrideEditor(({
        editor,
        tf: { normalizeNode },
    }) => {
        return {
            transforms: {
                normalizeNode: ([node, path]) => {
                    if (node.type === 'source-caption') {
                        const typedNode = node as TSourceCaptionElement;

                        if (!typedNode.align) {
                            editor.tf.setNodes({
                                ...node,
                                align: 'left',
                            }, { at: path });
                        }

                        if (!typedNode.sourceKind) {
                            editor.tf.setNodes({
                                ...node
                            }, { at: path });
                        }
                    }

                    normalizeNode([node, path]);
                }
            }
        };
    });
